import CustomToast from "../../utils/functions/CustomToast";
import HttpClient from "../../utils/functions/HttpClient";
import ServerEndpoints from "../../utils/constants/ServerEndpoints";
import Errors from "../../utils/constants/Errors";

const request = async (operation, { setApiError, setIsLoading } = {}) => {
  setApiError?.(false);

  try {
    return await operation();
  } catch (err) {
    setApiError?.(true);
    handleAddOnRequestError(err);
    throw err;
  } finally {
    setIsLoading?.(false);
  }
};

const normalizeQueryParams = (params = {}) => {
  const normalizedParams = { ...params };

  if (normalizedParams.pageSize != null && normalizedParams.size == null) {
    normalizedParams.size = normalizedParams.pageSize;
  }

  delete normalizedParams.pageSize;
  return normalizedParams;
};

const toNullableNumber = (value) => {
  if (value == null || value === "") {
    return null;
  }

  const parsedValue = Number(value);
  return Number.isNaN(parsedValue) ? null : parsedValue;
};

const normalizeMedia = (addOn = {}) => {
  if (addOn.media) {
    return {
      ...addOn.media,
      id: toNullableNumber(addOn.media.id),
    };
  }

  if (addOn.mediaId != null && addOn.mediaId !== "") {
    return { id: toNullableNumber(addOn.mediaId) };
  }

  return null;
};

const normalizeAddOn = (addOn = {}) => ({
  ...addOn,
  id: toNullableNumber(addOn.id),
  categoryId: toNullableNumber(addOn.categoryId),
  active: addOn.active ?? addOn.isActive ?? true,
  isActive: addOn.isActive ?? addOn.active ?? true,
  media: normalizeMedia(addOn),
});

const normalizeAddOnListResponse = (data) => ({
  ...data,
  content: (data?.content ?? []).map(normalizeAddOn),
});

const buildAddOnPayload = (addOn) => ({
  id: toNullableNumber(addOn.id),
  name: addOn.name,
  category: addOn.category,
  categoryId: toNullableNumber(addOn.categoryId),
  description: addOn.description,
  price: Number(addOn.price ?? 0),
  isActive: Boolean(addOn.isActive ?? addOn.active ?? true),
  addonType: addOn.addonType ?? addOn.type ?? null,
  mediaId: toNullableNumber(addOn.media?.id ?? addOn.mediaId),
});

export const getAllAddOns = async (
  setAddOnsData,
  params,
  setIsLoading,
  setApiError
) => {
  const response = await request(
    () =>
      HttpClient.getAxiosClient().get(`${ServerEndpoints.ADDONS_ENDPOINT}`, {
        params: normalizeQueryParams(params),
      }),
    { setApiError, setIsLoading }
  );

  if (response.status === 200) {
    setAddOnsData(normalizeAddOnListResponse(response.data));
  }
};

export const getAddOnById = async (
  setAddOnData,
  id,
  setIsLoading,
  setApiError
) => {
  const response = await request(
    () => HttpClient.getAxiosClient().get(`${ServerEndpoints.ADDONS_ENDPOINT}/${id}`),
    { setApiError, setIsLoading }
  );

  if (response.status === 200) {
    setAddOnData(normalizeAddOn(response.data));
  }
};

export const deleteAddOn = async (id, setState, setIsLoading, setApiError) => {
  const response = await request(
    () => HttpClient.getAxiosClient().delete(`${ServerEndpoints.ADDONS_ENDPOINT}/${id}`),
    { setApiError, setIsLoading }
  );

  if (response.status === 204) {
    CustomToast("Add-on successfully deleted", "success");
    setState?.((prev) => !prev);
  }
};

export const deleteAddOns = async (ids, setState, setIsLoading, setApiError) => {
  const response = await request(
    () =>
      HttpClient.getAxiosClient().delete(`${ServerEndpoints.ADDONS_ENDPOINT}`, {
        data: ids,
      }),
    { setApiError, setIsLoading }
  );

  if (response.status === 204) {
    CustomToast("Add-ons successfully deleted", "success");
    setState?.((prev) => !prev);
  }
};

export const updateAddOn = async (
  addOn,
  imageFile,
  setState,
  setApiError,
  setIsLoading
) => {
  const requestData = new FormData();
  requestData.append(
    "addOn",
    new Blob([JSON.stringify(buildAddOnPayload(addOn))], { type: "application/json" })
  );

  if (imageFile) {
    requestData.append("imageFile", imageFile);
  }

  const response = await request(
    () => HttpClient.getAxiosClient().put(`${ServerEndpoints.ADDONS_ENDPOINT}/${addOn.id}`, requestData),
    { setApiError, setIsLoading }
  );

  if (response.status === 200) {
    CustomToast("Add-on successfully updated", "success");
    setState?.((prev) => !prev);
  }

  return normalizeAddOn(response.data);
};

export const createAddOn = async (
  addOn,
  imageFile,
  setState,
  setApiError,
  setIsLoading
) => {
  const requestData = new FormData();
  requestData.append(
    "addOn",
    new Blob([JSON.stringify(buildAddOnPayload(addOn))], { type: "application/json" })
  );

  if (imageFile) {
    requestData.append("imageFile", imageFile);
  }

  const response = await request(
    () => HttpClient.getAxiosClient().post(`${ServerEndpoints.ADDONS_ENDPOINT}`, requestData),
    { setApiError, setIsLoading }
  );

  if (response.status === 201 || response.status === 200) {
    CustomToast("Add-on successfully created", "success");
    setState?.((prev) => !prev);
  }

  return normalizeAddOn(response.data);
};

const getErrorStatus = (err) => err?.response?.status;

const getBackendErrorMessage = (err) => {
  const data = err?.response?.data;

  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    return data.errors[0];
  }

  return data?.message || null;
};

const handleAddOnRequestError = (err) => {
  const backendMessage = getBackendErrorMessage(err);

  switch (getErrorStatus(err)) {
    case 400:
      CustomToast(backendMessage || "Please review your data and try again.", "error");
      return;
    case 401:
      CustomToast("Please sign in to continue.", "error");
      return;
    case 403:
      CustomToast(backendMessage || "You are not allowed to perform this action.", "error");
      return;
    case 404:
      CustomToast(backendMessage || "The add-on could not be found.", "error");
      return;
    case 409:
      CustomToast(backendMessage || "An add-on with the provided name already exists.", "error");
      return;
    case 503:
      CustomToast(Errors.SERVICE_UNAVAILABLE_ERROR || Errors.SERVICE_UNAVALAIBLE_ERROR, "error");
      return;
    default:
      showGenericRequestError(err);
  }
};

const showGenericRequestError = (err) => {
  if (err?.response == null) {
    CustomToast(Errors.API_ERROR, "error");
    return;
  }

  const backendMessage = getBackendErrorMessage(err);
  CustomToast(backendMessage || Errors.API_ERROR, "error");
};
